// Client-side mirror of the brand triage policy used by fig-verify.
// Kept in sync with supabase/functions/fig-verify/brandTriage.ts —
// every entry below has a corresponding key in APPROVE_BRANDS,
// EDGE_CASES, or DENY_BRANDS on the server. Re-sync whenever a brand
// is added or removed on the triage side so /brands doesn't lie to
// users about what FIG actually accepts.
//
// Display names use natural capitalization and parenthetical era
// qualifiers (e.g. "Juicy Couture (2001–2008)"). The Brands page
// strips parentheticals before routing to /brand/:name so the hub
// page still finds matching scans.

export const VINTAGE_BRAND_GROUPS: Record<string, string[]> = {
  // ─── Y2K (2000–2009) ─────────────────────────────────────────────────────
  "Y2K Denim": [
    "True Religion", "Seven For All Mankind", "Rock & Republic", "Citizens of Humanity",
    "Joe's Jeans", "Parasuco", "Frankie B", "Miss Me", "MEK", "AG Adriano Goldschmied",
    "Hudson", "Paper Denim & Cloth", "Earnest Sewn", "Antik Denim",
    "Marithé François Girbaud", "JNCO", "Diesel", "Fiorucci", "Pepe Jeans",
    "Apple Bottoms", "Paris Blues",
  ],
  "Y2K Women's Fashion": [
    "Juicy Couture (2001–2008)", "Baby Phat", "Bebe (1990s–2000s)",
    "BCBG Max Azria", "Cache", "Marciano by Guess", "Betsey Johnson", "Sue Wong",
    "XOXO", "Bisou Bisou", "Rampage", "Blumarine", "Miss Sixty (2000s)",
    "City Triangles", "Catch My i",
    "Joseph A. (Qu'est-ce que c'est silk?)", "J.A.C.",
    "August Silk",
  ],
  "Y2K Pop Culture": [
    "Von Dutch", "Ed Hardy", "Christian Audigier", "Affliction", "Coogi", "Heatherette",
  ],
  "Y2K Hip-Hop / Streetwear": [
    "FUBU", "Rocawear", "Sean John", "Phat Farm", "Ecko Unlimited", "Karl Kani",
    "Cross Colours", "Akademiks", "Southpole", "Mecca USA", "Enyce", "Pelle Pelle",
    "Avirex", "LRG", "Dada Supreme", "Vokal", "State Property", "G-Unit", "Wu-Wear",
    "DKNY (vintage)",
  ],
  "Y2K Skate / Surf": [
    "Stüssy", "Volcom", "Billabong", "Quiksilver", "Rip Curl", "Hurley", "O'Neill",
    "RVCA", "Element", "DC Shoes", "Etnies", "Emerica", "Zoo York", "Vans (vintage)",
    "Thrasher", "Santa Cruz", "Independent Truck Co.", "Powell-Peralta", "X-Large",
    "Alien Workshop", "Vision Street Wear", "Ocean Pacific", "Maui and Sons", "HUF",
  ],
  "Y2K Alt / Emo / Goth": ["Tripp NYC", "Lip Service", "Morbid Threads", "Kikwear", "Cyberdog"],

  // ─── Luxury ──────────────────────────────────────────────────────────────
  "Luxury French": [
    "Chanel", "Hermès", "Louis Vuitton", "Dior", "Yves Saint Laurent", "Celine",
    "Givenchy", "Balenciaga", "Lanvin", "Balmain", "Chloé", "Sonia Rykiel",
    "Jean Paul Gaultier", "Thierry Mugler", "Azzedine Alaïa", "Pierre Cardin",
    "André Courrèges", "Claude Montana",
  ],
  "Luxury Italian": [
    "Gucci", "Prada", "Versace", "Valentino", "Fendi", "Bottega Veneta",
    "Dolce & Gabbana", "Missoni", "Etro", "Emilio Pucci", "Salvatore Ferragamo",
    "Giorgio Armani", "Emporio Armani", "Moschino", "Roberto Cavalli", "Max Mara",
  ],
  "Luxury British": [
    "Burberry", "Alexander McQueen", "Vivienne Westwood", "Stella McCartney",
    "John Galliano", "Paul Smith", "Mulberry",
  ],
  "Modern Luxury": ["Loewe", "The Row"],

  // ─── Designers (by era / region) ─────────────────────────────────────────
  "American Designers": [
    "Calvin Klein (Collection)", "Ralph Lauren (Purple Label, Collection, RRL)",
    "Marc Jacobs", "Tom Ford", "Oscar de la Renta", "Carolina Herrera", "Donna Karan",
    "Halston", "Anna Sui", "Norma Kamali", "Bill Blass", "Geoffrey Beene",
    "Pauline Trigère", "Bob Mackie", "Stephen Sprouse", "Mary McFadden", "Adrian",
    "Anne Klein", "Oleg Cassini",
  ],
  "Japanese Designers": [
    "Comme des Garçons", "Issey Miyake", "Yohji Yamamoto", "Kenzo (vintage)",
    "Sacai", "Junya Watanabe", "Undercover", "Hysteric Glamour", "Kapital",
    "Visvim", "BAPE", "Neighborhood", "WTAPS", "Number (N)ine", "Human Made", "Needles",
    "AHCAHCUM.muchacha (Space and Time)",
  ],
  "Modern Designer": [
    "Acne Studios", "A.P.C.", "Kith", "Aimé Leon Dore", "Telfar", "Toteme",
    "Calypso St. Barth (original-era 1992–2017)",
  ],
  "1940s American Sportswear": [
    "Tina Leser", "Vera Maxwell", "B.H. Wragge", "Carolyn Schnurer", "Clare Potter",
    "Traina-Norell",
  ],
  "1940s Hollywood / LA Couture": [
    "Howard Greer", "Irene (Irene Lentz)",
  ],
  "1940s–50s Cocktail / Evening": [
    "Ceil Chapman", "Anne Fogarty",
  ],
  "50s–60s American Couture": [
    "Norman Norell", "Mainbocher", "Charles James", "Claire McCardell",
    "Bonnie Cashin", "James Galanos", "Hattie Carnegie", "Mollie Parnis",
    "Adele Simpson", "Don Loper", "Larry Aldrich", "Donald Brooks", "Rudi Gernreich",
  ],
  "60s British / European Mod": [
    "Mary Quant", "Biba", "Ossie Clark", "Zandra Rhodes", "Foale and Tuffin",
    "Jean Muir", "Paco Rabanne", "Emanuel Ungaro", "Nina Ricci", "Guy Laroche",
    "Madame Grès", "Paraphernalia",
  ],
  "Pre-war French Couture": [
    "Madeleine Vionnet", "Elsa Schiaparelli", "Jean Patou", "Lucien Lelong",
    "Paul Poiret", "Mariano Fortuny", "House of Worth", "Edward Molyneux",
    "Callot Soeurs", "Marcel Rochas", "Robert Piguet", "Maggy Rouff",
    "Jacques Fath", "Jacques Heim", "Jacques Doucet", "Jeanne Paquin", "Lucile",
    "Augustabernard", "Madeleine de Rauch",
  ],
  "Pre-war British Couture": [
    "Norman Hartnell", "Hardy Amies",
  ],
  "60s–90s French RTW Heritage": [
    "Georges Rech", "Synonyme by Georges Rech", "Unanyme by Georges Rech",
  ],

  // ─── Vintage essentials by category ──────────────────────────────────────
  "Denim Heritage": [
    "Levi's (always approve)", "Levi Strauss Vintage Clothing (LVC)",
    "Lee", "Wrangler", "Jordache",
    "Calvin Klein Jeans (vintage)", "Gloria Vanderbilt", "Sasson",
  ],
  "Workwear / Americana": [
    "Carhartt", "Dickies", "Filson", "Schott NYC", "Red Wing", "Ben Davis",
    "Pointer Brand", "Round House", "OshKosh B'Gosh", "Stan Ray",
  ],
  "Western": [
    "Nudie's Rodeo Tailors", "Manuel Couture", "Stetson", "H Bar C",
    "Rockmount Ranch Wear", "Panhandle Slim", "Tony Lama", "Lucchese", "Justin Boots",
  ],
  "Preppy / Ivy": [
    "Brooks Brothers (pre-2020)", "J. Press", "Polo Ralph Lauren (vintage)",
    "Izod", "Lacoste (vintage)", "Gant", "Talbots",
    "Vested Gentress", "The Villager", "Ladybug", "John Meyer of Norwich", "McMullen",
  ],
  "Sportswear (vintage)": [
    "Nike (vintage/collab)", "Adidas (vintage Trefoil)",
    "Champion (vintage Reverse Weave)", "Starter", "Fila (1990s)",
    "Reebok (1980s–90s)", "Puma (vintage)", "New Balance (vintage)",
    "Converse (vintage)", "Russell Athletic (vintage)", "Umbro (vintage)",
    "Kappa", "Diadora", "Ellesse", "Sergio Tacchini", "Le Coq Sportif",
    "Kangol", "Mitchell & Ness", "Pro Player", "Logo Athletic", "Apex One", "AND1 (vintage)",
  ],
  "Streetwear": [
    "Supreme", "Palace Skateboards", "The Hundreds", "Diamond Supply Co.",
    "Obey", "Stone Island", "C.P. Company", "Fear of God", "Off-White",
    "Stüssy (vintage)", "Noah", "Undefeated", "Alife",
  ],
  "Outdoor (vintage)": [
    "The North Face (vintage)", "Patagonia (vintage)", "Columbia (vintage)",
    "Eddie Bauer (pre-2000)", "L.L. Bean", "Woolrich", "Pendleton", "Filson",
    "REI (vintage)", "Sierra Designs", "Helly Hansen (vintage)", "Barbour",
    "Belstaff", "Penfield", "Fjällräven (vintage)",
  ],
  "Band Merch Blanks (vintage)": [
    "Brockum", "Giant", "Winterland", "Screen Stars (pre-1990s)", "Liquid Blue", "Wild Oats",
  ],
  "Hawaiian": ["Reyn Spooner", "Tori Richard", "Aloha Republic", "Kahala", "Avanti", "Sun Surf"],

  // ─── Mid-tier / specialty (additions from triage analysis) ───────────────
  "70s–90s American Dress / Sportswear": [
    "All That Jazz", "Nightway", "Studio I", "Eagle's Eye", "Storybook Knits",
    "Joseph Ribkoff", "Norm Thompson", "Bagatelle", "Oilily", "Daniel & Mayer",
    "Catalina", "Koret of California", "Russ Togs", "Evan Picone", "Leslie Fay",
    "Diane Freis", "Jones New York", "J.G. Hook", "Ann Tjian for Kenar",
    "Spenser Jeremy", "J.B. Too", "D.B.Y. Ltd.", "Stowe", "CDC",
    "Alain Toussaint", "Daniel Benjamin (Y2K)", "Ann Trinity (Y2K)",
    "Cormani (Cormani Collections)",
  ],
  "50s–60s American Dress / Sportswear": [
    "Lanz of Salzburg", "Lilli Ann", "Lilli Diamond", "Jonathan Logan",
    "Youth Guild", "R&K Originals", "Mr. Mort", "Henry Lee", "Jerry Gilden",
    "Toni Todd", "Vicky Vaughn", "Edith Flagg", "Howard Wolf",
  ],
  "50s–60s Menswear": [
    "Botany 500", "Hart Schaffner Marx", "Hickey Freeman", "Oxxford Clothes",
    "After Six", "Lord West", "Damon", "Manhattan Shirt Company", "Arrow",
  ],
  "Prairie / Boho (60s–70s)": [
    "Gunne Sax (Jessica McClintock)", "Jessica McClintock",
    "Young Edwardian by Arpeja", "Young Innocent by Arpeja",
    "Candi Jones California", "East West Musical Instruments",
  ],
  "60s–70s American Heritage": [
    "McGregor", "Robert Bruce", "Jantzen",
  ],
  "60s–70s Import-house Labels (suede / leather / shearling)": [
    "Exclusive Imports", "Exclusive Originals", "Imported Originals",
    "Berman's (The Leather Experts)", "Reed Sportswear", "The Tannery", "Tannery West",
    "Mighty-Mac", "Char by Charlotte",
  ],
  "Vintage Surf / Swim": [
    "Cole of California", "Birdwell Beach Britches", "Hang Ten",
    "Lightning Bolt", "Body Glove", "Rose Marie Reid", "DeWeese",
  ],
  "80s Designer Jeans / Casual": [
    "Generra", "Sergio Valente", "International Male",
  ],
  "Vintage Costume Jewelry (top tier)": [
    "Miriam Haskell", "Eisenberg (Original / Ice)", "Schreiner New York",
  ],
  "Vintage Costume Jewelry (mid)": [
    "Crown Trifari", "Coro / Corocraft", "Hobé", "Napier", "Vendome", "Weiss",
    "Sarah Coventry", "Monet",
  ],
  "Vintage Millinery / Hats": [
    "Lilly Daché", "Mr. John", "John-Frederics", "Sally Victor",
  ],
  "Vintage Fine Shoes": [
    "I. Miller", "Delman", "Herbert Levine / Beth Levine",
  ],
  "Vintage Handbags / Accessories": [
    "Walborg", "Josef Original", "Llewellyn", "Capezio",
  ],
  "1940s–50s Daily-wear": [
    "Suzy Perette", "Carlye", "L'Aiglon", "Whiting & Davis", "Andrew Geller",
    "Cricketeer", "Society Brand Clothes", "Sero",
  ],
  "Vintage Bridal / Formal": [
    "Alfred Angelo", "Dance-Allure by Alfred Angelo", "Bridalure by Alfred Angelo",
  ],
  "Vintage Lingerie / Loungewear": [
    "Cinema Etoile (original 80s/90s)", "Basil",
  ],
  "Specialty Prints / Scarves": [
    "Vera Neumann", "Liberty of London", "Marimekko",
  ],
  "International Heritage / Formal": [
    "Tadashi Shoji", "Jaeger", "Dino'z", "Noemi Fontana",
  ],
  "Premier Department-store Exclusives": [
    "Melanzona / Melanzana",
    // Bergdorf / Bendel / Bonwit double-labels boost any approved brand
    // (see triage's detectPremierStoreProvenance).
  ],
};

// Era-gated brands. Each entry tells the user when the vintage window
// passes and when modern production drops out. Numbers come straight from
// triage's EDGE_CASES Map.
export const EDGE_CASES: { brand: string; pass: string; fail: string }[] = [
  { brand: "Tommy Hilfiger",          pass: "Pre-2003",                                fail: "Post-2005" },
  { brand: "J.Crew",                   pass: "Pre-2010",                                fail: "Post-2012" },
  { brand: "Banana Republic",          pass: "Pre-2000",                                fail: "Post-2005" },
  { brand: "Chaps",                    pass: "—",                                       fail: "RL outlet diffusion — not accepted" },
  { brand: "Lauren Ralph Lauren",      pass: "—",                                       fail: "RL outlet / diffusion — not accepted" },
  { brand: "Ralph Lauren / Polo",      pass: "Pre-2005 mainline",                       fail: "Lauren RL outlet or Chaps" },
  { brand: "Champion",                 pass: "Pre-2000, Made in USA Reverse Weave",     fail: "Post-2010 mass market" },
  { brand: "Nike",                     pass: "Pre-2005 or collab",                      fail: "Current commodity basics" },
  { brand: "Adidas",                   pass: "Vintage Trefoil, pre-2000, Equipment",    fail: "Current commodity basics" },
  { brand: "Guess",                    pass: "Triangle logo, 1981–early 2000s",         fail: "Post-2005" },
  { brand: "Victoria's Secret",        pass: "1990s–early 2000s Gold Label / catalog",  fail: "Post-2010" },
  { brand: "Express / Limited Express",pass: "1980s–1990s",                             fail: "Post-2005" },
  { brand: "The Limited",              pass: "1980s–1990s",                             fail: "Post-2000" },
  { brand: "Esprit",                   pass: "1980s–early 1990s",                       fail: "Post-2000" },
  { brand: "United Colors of Benetton",pass: "1980s–early 1990s",                       fail: "Post-2000" },
  { brand: "Levi's",                   pass: "Always approve (mid-tier baseline)",      fail: "—" },
  { brand: "Fila",                     pass: "1990s Made-in-Italy",                     fail: "Post-2010" },
  { brand: "Reebok",                   pass: "1980s–90s Pump / Classic era",            fail: "Post-2005" },
  { brand: "Eddie Bauer",              pass: "Pre-2000",                                fail: "Post-2005" },
  { brand: "Juicy Couture",            pass: "2001–2008 founders era",                  fail: "Post-2010 Kohl's licensing" },
  { brand: "Bebe",                     pass: "1990s–2000s",                             fail: "Post-2010" },
  { brand: "Brooks Brothers",          pass: "Pre-2020, esp. Made in USA",              fail: "Post-2020" },
  { brand: "Liz Claiborne",            pass: "Original 1976–2007 line",                 fail: "Post-2010 JCPenney version" },
  { brand: "Anthropologie",            pass: "Pre-2015 with distinctive design",        fail: "Post-2018" },
  { brand: "Miss Sixty",               pass: "2000s original era",                      fail: "Post-2010" },
  { brand: "White Stag",               pass: "Pre-2003",                                fail: "Post-2003 (Walmart private label)" },
  { brand: "Hanes",                    pass: "Pre-1995 single-stitch Beefy-T era",      fail: "Modern commodity blanks" },
  { brand: "Fruit of the Loom",        pass: "Pre-1995 single-stitch",                  fail: "Modern commodity blanks" },
  { brand: "Nasty Gal",                pass: "Pre-2019 original era",                   fail: "Post-2019 (Boohoo acquisition)" },
  { brand: "Karen Millen",             pass: "Pre-2019 original",                       fail: "Post-2019 (Boohoo version)" },
  { brand: "Dorothy Perkins",          pass: "Pre-2019 original",                       fail: "Post-2019 (Boohoo version)" },
  { brand: "Wallis",                   pass: "Pre-2019 original",                       fail: "Post-2019 (Boohoo version)" },
  { brand: "Burton Menswear",          pass: "Pre-2019 original",                       fail: "Post-2019 (Boohoo version)" },
  { brand: "Arizona Jean Co.",         pass: "1990s vintage",                           fail: "Modern (JCPenney private label)" },
  { brand: "Bugle Boy",                pass: "1977–2001 Lowry-era (80s parachute, 90s casual)", fail: "Post-2008 Iconix licensing" },
  { brand: "Joe Boxer",                pass: "Pre-2001 original (80s–90s loungewear)",  fail: "Post-2002 Kmart / Sears licensing" },
  { brand: "Wilson Leather",           pass: "Pre-1995 (heavier hides, US/import)",     fail: "Post-2000 mall-tier commodity" },
  { brand: "Metaphor (Sears)",         pass: "Pre-2005, Made in USA (Y2K era)",         fail: "Post-2010 mall-tier private label" },
];

// Brands FIG will not authenticate. Mirrors triage's DENY_BRANDS set.
export const HARD_FAIL_GROUPS: Record<string, string[]> = {
  "Ultra Fast Fashion": [
    "Shein", "Romwe", "Temu", "Wish", "Cider", "Fashion Nova", "Halara", "Emmiol",
    "Urbanic", "PatPat", "Cupshe", "Dresslily", "Rosegal", "Zaful", "ChicWish",
    "Edikted", "YesStyle", "Fairyseason",
  ],
  "Inditex": ["Zara", "Pull & Bear", "Bershka", "Stradivarius", "Oysho", "Lefties"],
  "H&M Group": ["H&M", "Monki", "Weekday", "COS", "& Other Stories", "ARKET"],
  "Boohoo Group": [
    "Boohoo", "BoohooMAN", "PrettyLittleThing", "MissPap",
    // Post-acquisition versions only; pre-2019 originals are in EDGE_CASES above.
    "Nasty Gal (post-2019)", "Karen Millen (post-2019)", "Dorothy Perkins (post-2019)",
    "Wallis (post-2019)", "Burton Menswear (post-2019)",
  ],
  "ASOS Own Brands": ["ASOS Design", "ASOS Edition", "Collusion", "Reclaimed Vintage (ASOS)"],
  "Other Chains": [
    "Forever 21", "Primark", "New Look", "River Island", "C&A", "Cotton On",
    "Supré", "Jay Jays", "Kiabi", "Pimkie", "LC Waikiki", "DeFacto", "Koton",
  ],
  "Low-Quality Mall": [
    "Charlotte Russe", "Wet Seal", "Rue 21", "Rainbow", "Body Central", "DEB",
    "Mandee", "Cato", "Dots", "Papaya", "Love Culture", "Agaci", "Vanity",
    "Windsor", "Garage", "Dynamite", "Francesca's",
    "Fashion Bug", "Alfred Dunner", "R&M Richards", "Rafaella",
  ],
  "Modern Commodity Blanks": [
    "Gildan (modern)", "Jerzees", "Anvil", "Port & Company", "Delta", "Next Level Apparel",
    // Hanes / Fruit of the Loom pre-1995 are in EDGE_CASES; modern is deny.
  ],
  "Target Private Label": [
    "Wild Fable", "Universal Thread", "A New Day", "Goodfellow & Co.", "JoyLab",
    "All in Motion", "Ava & Viv", "Stars Above", "Colsie", "Cherokee", "Merona", "Mossimo",
  ],
  "Walmart Private Label": [
    "Time and Tru", "George", "Terra & Sky", "No Boundaries", "Free Assembly",
    "Faded Glory", "Athletic Works", "Kathie Lee Gifford",
  ],
  "Amazon Private Label": [
    "Amazon Essentials", "Amazon Aware", "The Drop", "Daily Ritual", "Goodthreads", "Lark & Ro",
  ],
  "Kohl's Private Label": [
    "Sonoma Goods for Life", "SO", "Croft & Barrow", "Tek Gear", "Urban Pipeline", "Apt. 9",
  ],
  "JCPenney Private Label": [
    "St. John's Bay", "Worthington", "a.n.a", "Stafford",
    // Arizona Jean Co. pre-2000 in EDGE_CASES.
  ],
  "Department-store Budget Labels": [
    "Bobbie Brooks", "Sag Harbor", "Cathy Daniels", "Notations",
  ],
  "Mall / Modern Western (not heritage)": [
    "Beverly Hills Polo Club", "Roper", "Karman Western",
  ],
  "Other Excludes": [
    "Under Armour", "Skechers", "Fabletics", "Athleta", "Gap", "Abercrombie & Fitch",
    "Hot Topic", "Free People", "Uniqlo", "Topshop", "Delia's", "PacSun", "Hollister", "Mango",
  ],
  "Self-identified Fast Fashion": [
    // Brands that explicitly market themselves as fast fashion in their own
    // wholesale documentation OR operate on a fast-fashion production model
    // despite boutique-coded branding. Distinct from the ultra-fast-fashion
    // category because the names sound contemporary, vintage-inspired, or
    // boutique — they're easy to mistake for higher-tier brands.
    "Perseption",
    // LA wholesale fast fashion
    "Honey Punch", "Lush Clothing", "Cotton Candy LA", "Akira (Chicago)", "Naked Wardrobe",
    // DTC ecommerce fast fashion
    "Princess Polly", "Lulus", "Tobi", "Dolls Kill",
    // Australian fast fashion
    "Showpo", "Meshki", "Beginning Boutique", "Verge Girl",
    // UK fast fashion
    "Oh Polly", "In The Style", "Quiz Clothing",
    // Vintage-coded branding, fast-fashion production model
    "ModCloth",
    // Borderline budget denim still in continuous production
    "L.A. Blues",
  ],
};
